/**
 * React hook for persisted settings
 */
import { useState, useEffect, useCallback } from 'react';
import { saveSetting, loadSetting } from '../storage/indexeddb';
import type { PillMode } from '../components/PillChoice';

export type Theme = 'dark' | 'light';

interface Settings {
  pillMode: PillMode | null;
  hasChosenPill: boolean;
  theme: Theme;
}

interface UseSettingsResult {
  settings: Settings;
  loading: boolean;
  setPillMode: (mode: PillMode) => Promise<void>;
  setTheme: (theme: Theme) => Promise<void>;
  toggleTheme: () => Promise<void>;
  resetSettings: () => Promise<void>;
}

// Check localStorage first for quick access (IndexedDB can be slow on mobile)
function getQuickPillMode(): PillMode | null {
  try {
    const stored = localStorage.getItem('aos_pillMode');
    return stored as PillMode | null;
  } catch {
    return null;
  }
}

const quickPill = getQuickPillMode();
const DEFAULT_SETTINGS: Settings = {
  pillMode: quickPill,
  hasChosenPill: quickPill !== null,
  theme: 'dark',
};

export function useSettings(): UseSettingsResult {
  const [settings, setSettings] = useState<Settings>(DEFAULT_SETTINGS);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function load() {
      try {
        const pillMode = await loadSetting<PillMode>('pillMode');
        const theme = await loadSetting<Theme>('theme') || 'dark';
        setSettings({
          pillMode,
          hasChosenPill: pillMode !== null,
          theme,
        });
      } catch (err) {
        console.error('Failed to load settings:', err);
      } finally {
        setLoading(false);
      }
    }
    load();
  }, []);

  const setPillMode = useCallback(async (mode: PillMode) => {
    // Save to localStorage for quick access on next load
    try {
      localStorage.setItem('aos_pillMode', mode);
    } catch {
      // Ignore localStorage errors
    }
    await saveSetting('pillMode', mode);
    setSettings(prev => ({
      ...prev,
      pillMode: mode,
      hasChosenPill: true,
    }));
  }, []);

  const setTheme = useCallback(async (theme: Theme) => {
    await saveSetting('theme', theme);
    setSettings(prev => ({ ...prev, theme }));
  }, []);

  const toggleTheme = useCallback(async () => {
    const newTheme = settings.theme === 'dark' ? 'light' : 'dark';
    await setTheme(newTheme);
  }, [settings.theme, setTheme]);

  const resetSettings = useCallback(async () => {
    await saveSetting('pillMode', null);
    setSettings(DEFAULT_SETTINGS);
  }, []);

  return { settings, loading, setPillMode, setTheme, toggleTheme, resetSettings };
}
